import { useEffect, useState } from 'react';
import { Link } from 'react-router-dom';
import { api, Card, CardTitle, CardDescription, useAuth, titleCaseName, brandDisplayName } from '@aralere/shared-ui';
import type { TaxReviewStatusResponse } from '@aralere/shared-types';
import { ADMIN_PARENT_ORDER, ADMIN_SECTIONS, sectionShown } from '../../adminSections';
import { QuickTimer } from '../../components/QuickTimer';
import { TAX_REVIEW_REASON_LABELS } from './taxCategory';

// The admin dashboard card grid. Cards derive from the single ADMIN_SECTIONS source
// (adminSections.ts) — the SAME list that builds the top nav — so every top tab has a card and
// the two surfaces can never fall out of step. (Previously this was a hardcoded showcase that had
// silently lagged the feature set for many sessions: no Packages, Vendors, Expenses, or Settings.)
// s49: the quick Start/End timer sits right under the header — the owner's low-friction road-side
// logging path (start/stop here; the full entry info completes on the Time page).
// s103 (04 #93 slice (a)): the grid is grouped under the five nav parents (Work · Billing ·
// Clients · Catalog · System) — same source, same order as the top-nav menus, owner-ruled.
// s231 (04 #160 WS-B; 02i T4): the TAX REVIEW banner. The server's pure trigger decides, per brand, on
// the brand's own calendar (GET mgmt-tax-review?view=status: December 1 until completed, a profile
// deployed with a future-dated rule, a profile reviewed over a year ago); the dashboard only renders
// what it is told and links to the review. Best-effort: a failed status read shows nothing.
export function AdminDashboard(): JSX.Element {
  const { user, collectsForOthers } = useAuth();
  const [taxStatus, setTaxStatus] = useState<TaxReviewStatusResponse | null>(null);

  useEffect(() => {
    let alive = true;
    void (async () => {
      try {
        const res = await api.admin.taxReviewStatus();
        if (alive) setTaxStatus(res);
      } catch {
        if (alive) setTaxStatus(null);
      }
    })();
    return () => {
      alive = false;
    };
  }, []);

  const dueBrands = (taxStatus?.brands ?? []).filter((b) => b.status.due);

  return (
    <div className="space-y-8">
      <header>
        <h1 className="font-display text-3xl tracking-tight">Admin Dashboard</h1>
        <p className="text-sm text-[var(--color-muted)]">
          Signed in as {user?.displayName ? titleCaseName(user.displayName) : ''}
        </p>
      </header>

      <QuickTimer mode="dashboard" />

      {dueBrands.length > 0 ? (
        // One slim card, one line per due brand (owner, s231 fix pass: the first cut was two full-height
        // cards). The headline carries the reason when there is one; the generic reason labels otherwise.
        <Card className="bg-[var(--tint-warn-bg)]">
          <div className="space-y-1.5 text-sm" aria-label="Tax review due">
            {dueBrands.map((b) => (
              <div key={b.businessId} className="flex flex-wrap items-baseline justify-between gap-x-4 gap-y-0.5">
                <span className="min-w-0">
                  <span className="font-semibold text-[var(--tint-warn-text)]">Tax review due · {brandDisplayName(b.businessName)}</span>
                  <span className="text-[var(--color-muted)]">
                    {' — '}
                    {b.headline ?? `${b.status.reasons.map((r) => TAX_REVIEW_REASON_LABELS[r]).join(' · ')}.`}
                    {b.taxReviewCompletedAt ? ` Last completed ${b.taxReviewCompletedAt.slice(0, 10)}.` : ' Never completed.'}
                  </span>
                </span>
                <Link
                  to={`/admin/tax-review?business=${encodeURIComponent(b.businessId)}`}
                  className="shrink-0 text-sm font-medium underline decoration-dotted underline-offset-2 hover:decoration-solid"
                >
                  Open the review
                </Link>
              </div>
            ))}
          </div>
        </Card>
      ) : null}

      {ADMIN_PARENT_ORDER.map((parent) => {
        // 04 #125 WS-B: the same conditional flag the nav uses, so the two surfaces cannot disagree.
        const cards = ADMIN_SECTIONS.filter((s) => s.card !== false && s.parent === parent && sectionShown(s, { collectsForOthers }));
        if (cards.length === 0) return null;
        return (
          <section key={parent} className="space-y-3">
            <h2 className="text-xs font-medium uppercase tracking-[0.08em] text-[var(--color-muted)]">{parent}</h2>
            <div className="grid gap-4 md:grid-cols-3">
              {cards.map((s) =>
                s.comingSoon ? (
                  <Card key={s.to} className="h-full opacity-60">
                    <CardTitle>{s.label}</CardTitle>
                    <CardDescription>{s.description}</CardDescription>
                  </Card>
                ) : (
                  <Link key={s.to} to={s.to} className="block">
                    <Card className="h-full transition hover:shadow-md">
                      <CardTitle>{s.label}</CardTitle>
                      <CardDescription>{s.description}</CardDescription>
                    </Card>
                  </Link>
                ),
              )}
            </div>
          </section>
        );
      })}
    </div>
  );
}
